import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import LanguageToggle from "@/components/language-toggle";
import { toast } from "sonner";
import { Sparkle, Heart } from "@phosphor-icons/react";

export default function PublicReferral() {
  const { code } = useParams();
  const { t } = useLang();
  const [amb, setAmb] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", country: "United Kingdom", preferred_contact: "whatsapp", consent: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/ref/public/${code}`)
      .then((r) => setAmb(r.data))
      .catch(() => setNotFound(true));
  }, [code]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Consent required");
    setLoading(true);
    try {
      await api.post(`/ref/public/${code}/submit`, form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-[#0d3b66]">Referral link not found.</div>;
  if (!amb) return <div className="min-h-screen flex items-center justify-center text-[#0d3b66]">{t.common.loading}</div>;

  return (
    <div className="min-h-screen bg-[#f6f2ea]">
      <header className="max-w-4xl mx-auto px-6 md:px-10 py-6 flex justify-between items-center">
        <Link to="/" className="font-serif-display text-2xl text-[#0d3b66] flex items-center gap-2">
          <Sparkle size={20} weight="fill" color="#f4a261" /> {t.brand}
        </Link>
        <LanguageToggle />
      </header>
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-8">
        <div className="rounded-3xl bg-white border border-[#e6dfd2] overflow-hidden shadow-[0_20px_50px_-30px_rgba(13,59,102,0.35)]">
          <div className="p-8 md:p-12 bg-[#0d3b66] text-white">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#f4a261]">
              <Heart size={14} weight="fill" color="#f4a261" /> Personal invitation
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-4">{t.publicRef.title}</h1>
            <p className="mt-4 text-white/80 max-w-lg">
              {t.publicRef.sub.replace("{name}", amb.ambassador_name)}
            </p>
          </div>

          <div className="p-8 md:p-12">
            {done ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#0d3b66] flex items-center justify-center">
                  <Heart size={28} weight="fill" color="#f4a261" />
                </div>
                <h2 className="font-serif-display text-3xl mt-6">{t.publicRef.thanks}</h2>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="pubref-first-name" required placeholder={t.friends.form.firstName} value={form.first_name} onChange={set("first_name")} className="input" />
                  <input data-testid="pubref-last-name" required placeholder={t.friends.form.lastName} value={form.last_name} onChange={set("last_name")} className="input" />
                </div>
                <input data-testid="pubref-phone" required placeholder={t.friends.form.phone} value={form.phone} onChange={set("phone")} className="input" />
                <input data-testid="pubref-email" type="email" placeholder={t.friends.form.email} value={form.email} onChange={set("email")} className="input" />
                <select data-testid="pubref-preferred" value={form.preferred_contact} onChange={set("preferred_contact")} className="input">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone">{t.friends.form.phone}</option>
                  <option value="email">{t.friends.form.email}</option>
                </select>
                <label className="flex items-start gap-3 text-sm text-[#3b4b5a] cursor-pointer">
                  <input data-testid="pubref-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-1" />
                  <span>{t.publicRef.consent}</span>
                </label>
                <button data-testid="pubref-submit-btn" type="submit" disabled={loading} className="w-full px-6 py-3.5 rounded-full bg-[#0d3b66] text-[#f4a261] font-medium disabled:opacity-60 hover:bg-[#071f36]">
                  {loading ? t.common.loading : t.publicRef.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <style>{`
        .input { width:100%; padding:12px 16px; border-radius:12px; background:white; border:1px solid #e6dfd2; font-size:14px; outline:none; }
        .input:focus { border-color:#0d3b66; box-shadow:0 0 0 3px rgba(13,59,102,0.08); }
      `}</style>
    </div>
  );
}
